import type { AutoUncleCarInfo } from "@/types/autoUncle";
import type { VehicleDetails } from "@/types/vehicleDetails";

/**
 * Convert the backend vehicle-lookup envelope into VehicleDetails.
 *
 * Shared by BOTH lookup flows (dealer post-an-ad and public valuation), so any field
 * added here reaches both.
 */
export const convertAutoUncleToVehicleDetails = (
  carInfo: AutoUncleCarInfo
): Partial<VehicleDetails> => {
  // Check if it's an electric vehicle
  const isElectric = carInfo.fuel === "El" || carInfo.fuel === "Elbil" || carInfo.fuel === "ELECTRIC";

  // Map color - already validated in backend, use directly if present
  const color = carInfo.color || undefined;

  // Map horsepower only if it has a valid value (not null, undefined, or 0)
  const horsepower = carInfo.hp && carInfo.hp !== 0
    ? carInfo.hp.toString()
    : undefined;

  // Map engine_size 1:1 - already validated in backend, use directly if present
  const engineVolume = carInfo.engineVolume || undefined;

  // Map doors - only if valid value
  const doors = carInfo.doors && carInfo.doors > 0 ? carInfo.doors : undefined;

  // Map body - already validated against the chip vocabulary in the backend
  const bodyType = carInfo.body || undefined;

  // Normalize brand/make names to match system expectations.
  // Note: the backend already normalizes these; this is a defensive second pass for the
  // handful of aliases the makes catalog needs.
  const normalizeMake = (brand: string): string => {
    if (!brand) return brand;
    const normalized = brand.trim();
    const makeMappings: Record<string, string> = {
      "Mercedes-Benz": "Mercedes",
      "Mercedes Benz": "Mercedes",
      "MB": "Mercedes",
      "VW": "Volkswagen",
      "Škoda": "Skoda",
      "Honda Powersports": "Honda Motorcycles",
      "Suzuki Cycles": "Suzuki Motorcycles",
      "BMW Motorrad": "BMW Motorcycles",
    };
    return makeMappings[normalized] || normalized;
  };

  // Transmission comes from the backend's mapped `transmission` field.
  // EVs are always automatic regardless of what the provider reports.
  let transmission: string | undefined;
  if (isElectric) {
    transmission = "Automat";
  } else if (carInfo.transmission && carInfo.transmission.trim() !== "") {
    transmission = carInfo.transmission;
  }

  // Map electric drive range - only if valid value
  const electricDriveRange = carInfo.electricDriveRange && carInfo.electricDriveRange > 0
    ? carInfo.electricDriveRange
    : undefined;

  // Engine value derived from the lookup.
  //
  // EVs are marked "Electric" here rather than relying on useVehicleForm's auto-set,
  // which only runs inside handleSelectChange when the USER picks a fuel chip - on the
  // auto-load path fuelType arrives through initialData, so that branch never fires and
  // EVs ended up with no engine at all. The Motor chip group stays hidden for EVs by
  // design, so this is a stored value rather than a visible selection.
  //
  // Combustion prefill is cars-only: car.info reports MC engines as bare litre decimals
  // ("0.5") while every MC catalog chip is cc-with-layout ("650cc Parallel-Twin"), so a
  // match is impossible and the fallback would only pre-type meaningless noise.
  const engineFromLookup = isElectric
    ? "Electric"
    : carInfo.vehicleType !== "mc" && carInfo.engine && carInfo.engine.trim() !== ""
      ? carInfo.engine.trim()
      : undefined;

  const result = {
    make: carInfo.brand && carInfo.brand.trim() !== "" ? normalizeMake(carInfo.brand) : undefined,
    model: carInfo.car_model && carInfo.car_model !== "" ? carInfo.car_model : undefined,
    variant: carInfo.variant && carInfo.variant !== "" ? carInfo.variant : undefined,
    trim: carInfo.trim && carInfo.trim !== "" ? carInfo.trim : undefined,
    year: carInfo.year !== undefined && carInfo.year !== null && carInfo.year !== 0 ? carInfo.year : undefined,
    fuelType: carInfo.fuel && carInfo.fuel !== "" ? carInfo.fuel : undefined,
    // "Electric" for EVs; otherwise the raw car.info descriptor, which DetailsSection
    // upgrades to a canonical catalog chip once the makes catalog has loaded
    // (see resolveEngineOnCatalogLoad). Derived above.
    engine: engineFromLookup,
    bodyType: bodyType,
    transmission: transmission,
    // The backend sends raw km from the newest inspection reading; the app works in "mil".
    mileage: carInfo.km ? Math.round(carInfo.km / 10) : 0,
    // Equipment now arrives pre-mapped to exact checklist keys from the backend.
    equipment: carInfo.equipment && carInfo.equipment.length > 0 ? carInfo.equipment : undefined,
    vin: carInfo.vin && carInfo.vin !== "" ? carInfo.vin : undefined,
    regNumber: carInfo.lpn && carInfo.lpn !== "" ? carInfo.lpn.toUpperCase() : undefined,
    color: color,
    horsepower: horsepower,
    engineVolume: engineVolume,
    doors: doors,
    electricDriveRange: electricDriveRange,
    // Car/MC mode is driven by the provider's vehicle_type when it is one we support.
    // Unsupported values never reach here (the backend drops them), so an absent value
    // means "fall back to make-based isMC inference".
    vehicleType: carInfo.vehicleType,
    emissionClass: carInfo.emissionClass || undefined,
    upholstery: carInfo.upholstery || undefined,
    origin: carInfo.origin || undefined,
    // Raw tri-state from the provider. NOT derived from `origin`, which is lossy:
    // it collapses "imported" and "unknown" into the same empty value.
    isNotImported: carInfo.isNotImported || undefined,
    owners: carInfo.owners && carInfo.owners > 0 ? carInfo.owners : undefined,
    // Smart365-merged envelope fields. The pre-selector hand-built widget literals
    // copied these four; dropping them here sent registration_date:"" on post-an-ad
    // estimations (a pricing killer) and regressed seats/tax/inspection prefill.
    // Semantics replicate those old literals exactly.
    registrationDate: carInfo.registrationDate ?? undefined,
    lastInspectionDate: carInfo.lastInspectionDate ?? undefined,
    // Defensive typeof guard: the provider has been seen sending non-numeric junk here.
    annualVehicleTax:
      carInfo.annualVehicleTax != null && typeof carInfo.annualVehicleTax === "number"
        ? carInfo.annualVehicleTax
        : undefined,
    seats: carInfo.seats && carInfo.seats !== 0 ? carInfo.seats : undefined,
  };

  return result;
};

/**
 * Build the VehicleDetails patch applied after a successful lookup.
 *
 * This exists so the widgets do not each hand-enumerate the field list. Three separate
 * hand-built object literals used to do that, and any field added to the lookup response
 * was silently dropped unless every one of them was updated in lockstep. Route all lookup
 * -> form mapping through here.
 */
export const toVehicleDetailsPatch = (
  carInfo: AutoUncleCarInfo
): Partial<VehicleDetails> => {
  const mapped = convertAutoUncleToVehicleDetails(carInfo);

  // Drop undefined values so callers can spread this over existing state without
  // blanking fields the user already filled in.
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(mapped)) {
    if (value !== undefined) patch[key] = value;
  }
  return patch as Partial<VehicleDetails>;
};
